import { useState, useRef } from "react";
import { registrarAnexoSolicitacao } from "../api/functions";
import { Download, Upload } from "lucide-react";

const BRAND = {
  primary: "#00B4A6",
  secondary: "#007A8C",
  dark: "#004D5C",
  light: "#E6F9F8",
  white: "#FFFFFF",
};

export default function SecaoAnexosSolicitacao({ tarefa, onAnexoAdicionado, podeAdicionar = false, usuarioEmail = "" }) {
  const [uploading, setUploading] = useState(false);
  const [erro, setErro] = useState("");
  const inputRef = useRef(null);

  const anexos = tarefa?.anexos || [];
  const podeAdicionarAnexo = podeAdicionar && anexos.length < 5;

  const getIconeArquivo = (tipo) => {
    if (tipo.startsWith("image/")) return "🖼️";
    if (tipo === "application/pdf") return "📄";
    return "📎";
  };

  const formatarTamanho = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatarData = (dataISO) => {
    try {
      return new Date(dataISO).toLocaleDateString("pt-BR", {
        day: "2-digit", month: "2-digit", year: "2-digit",
        hour: "2-digit", minute: "2-digit"
      });
    } catch { return dataISO; }
  };

  const handleFileSelect = async (e) => {
    const arquivo = e.target.files?.[0];
    if (!arquivo) return;

    if (!arquivo.type.startsWith("image/") && arquivo.type !== "application/pdf") {
      setErro("Apenas PDF e imagens são permitidos");
      return;
    }
    if (arquivo.size > 10 * 1024 * 1024) {
      setErro("Arquivo deve ter no máximo 10MB");
      return;
    }

    setUploading(true);
    setErro("");

    try {
      const base64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (ev) => resolve(ev.target.result.split(",")[1]);
        reader.readAsDataURL(arquivo);
      });

      const res = await registrarAnexoSolicitacao({
        tarefa_id: tarefa.id,
        nome: arquivo.name,
        arquivo_base64: base64,
        tipo: arquivo.type,
        tamanho: arquivo.size,
        enviado_por: usuarioEmail || "Solicitante"
      });

      if (res?.success) {
        onAnexoAdicionado?.(res.anexo);
      } else {
        setErro(res?.error || "Erro ao enviar anexo");
      }

      if (inputRef.current) inputRef.current.value = "";
    } catch (err) {
      setErro("Erro ao enviar: " + (err.message || "desconhecido"));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{ background: BRAND.white, borderRadius: "14px", padding: "20px", marginTop: "20px", border: "1.5px solid #e5e5e5" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
        <h3 style={{ margin: 0, color: BRAND.dark, fontSize: "15px", fontWeight: "700" }}>📎 Anexos</h3>
        {podeAdicionarAnexo && (
          <button onClick={() => inputRef.current?.click()} disabled={uploading}
            style={{ display: "flex", alignItems: "center", gap: "6px", padding: "8px 14px", background: uploading ? "#ccc" : `linear-gradient(135deg, ${BRAND.primary}, ${BRAND.secondary})`, color: BRAND.white, border: "none", borderRadius: "8px", fontSize: "12px", fontWeight: "600", cursor: uploading ? "not-allowed" : "pointer" }}>
            <Upload size={14} />
            {uploading ? "Enviando..." : "Adicionar"}
          </button>
        )}
        {!podeAdicionarAnexo && anexos.length >= 5 && (
          <span style={{ fontSize: "12px", color: "#999", fontStyle: "italic" }}>Limite de 5 anexos atingido</span>
        )}
      </div>

      <input ref={inputRef} type="file" accept="application/pdf,image/*" onChange={handleFileSelect} style={{ display: "none" }} disabled={uploading || !podeAdicionarAnexo} />

      {erro && <div style={{ background: "#fee2e2", color: "#dc2626", padding: "10px 12px", borderRadius: "8px", fontSize: "12px", marginBottom: "12px" }}>{erro}</div>}

      {anexos.length === 0 ? (
        <p style={{ textAlign: "center", color: "#999", fontSize: "13px", margin: 0 }}>Nenhum anexo ainda</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {anexos.map((anexo, idx) => (
            <div key={idx} style={{ display: "flex", alignItems: "center", gap: "12px", padding: "12px", background: "#f9f9f9", border: "1px solid #e5e5e5", borderRadius: "8px" }}>
              <span style={{ fontSize: "16px" }}>{getIconeArquivo(anexo.tipo)}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <a href={anexo.url} target="_blank" rel="noopener noreferrer"
                  style={{ fontSize: "13px", fontWeight: "600", color: BRAND.primary, textDecoration: "none", display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                  title={anexo.nome}>{anexo.nome}</a>
                <div style={{ fontSize: "11px", color: "#999", marginTop: "2px" }}>
                  {formatarTamanho(anexo.tamanho)} • {anexo.enviado_por} • {formatarData(anexo.enviado_em)}
                </div>
              </div>
              <a href={anexo.url} target="_blank" rel="noopener noreferrer"
                style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "32px", height: "32px", borderRadius: "6px", background: BRAND.light, color: BRAND.primary, textDecoration: "none", cursor: "pointer" }}
                title="Download">
                <Download size={14} />
              </a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
